import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { eventDetailStore } from './eventDetail';
import { IAppEnv } from '../appEnvironment';
import { EventDataStore, IEventDataStore } from './event';
import { gameGroupStore } from './gameGroup';
import { gameStore } from './game';
import { gameDetailStore, createOdds, convertOdds2OddListItem, IgameDetailStore } from './gameDetail';
import { getEventDetailParamFromKey } from './mainCategoryMenu';
import { isObservableArray, observable } from 'mobx';
import { ProgressStateModelForStore, IGameGroup, createTipIdent, ProgressStateModel, rootCategoryId, IRenameTipData, getCompetitorNamesFromEventName, getRenameTipDataFromCompetitorNames, selectionVisualisationFlags2GameBusinessType, isOdds1x2, isTipList, isBelowOver, isHandicap1x2, isSpreadOrHandicap12 } from 'eTip--common';
import { canConvertTipName } from '../../eTipCommon/utils/eventsUtils';
import { parseTipIdent } from '../../eTipCommon/models/event';
import { LiveEventStore } from './live/liveEvent';

export const eventDetailKeyModel = types.model({
    eventId: types.number,
    categoryId: types.optional(types.string, rootCategoryId),
    detailId: types.number,
    isLive: types.boolean
});

export const eventListWithDetailModel = types
    .model('eventListWithDetailModel', {
        events: types.optional(types.array(EventDataStore), []), //create map and rows, ! not always filled!
        detail: types.maybeNull(eventDetailStore),
        liveDetail: types.maybeNull(LiveEventStore),
        isDetailShown: types.optional(types.boolean, false),
        defaults: types.maybeNull(eventDetailKeyModel),
        highlightedTips: types.optional(types.array(types.string), []),
        overallState: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        lastReloadTime: types.maybeNull(types.number)
    })
    .views(self => {
        const env = getEnv<IAppEnv>(self);

        return {
            get countDownTime() {
                return 1000;
            },

            get refreshTime() {
                return env.settings.topBetsRefreshTime;
            },

            get canShowOddsPopularity() {
                return false;
            },

            createGameGroups(eventId: number, eventName: string, gameGroups: IGameGroup[], defaultEventId?: number) {

                let renameTipData: IRenameTipData = getRenameTipDataFromCompetitorNames(getCompetitorNamesFromEventName(eventName));

                return gameGroups.map((g, groupIdx) =>
                    gameGroupStore.create(<SnapshotOut<typeof gameGroupStore>>{
                        ID: g.ID,
                        Name: g.Name,
                        Games: g.Games.map((gg, gameIdx) => {

                            let gameTipRenameData = canConvertTipName(gg) ? renameTipData : null;
                            let businessType = selectionVisualisationFlags2GameBusinessType(gg.SelectionVisualisationFlags);

                            return gameStore.create(<SnapshotOut<typeof gameStore>>{
                                ID: gg.ID,
                                Name: gg.Name,
                                BusinessType: businessType,
                                Sequence: gg.Sequence,
                                IsDetails: gg.IsDetails,
                                CombRateMin: gg.Details[0].CombRateMin,
                                CombCountMin: gg.Details[0].CombCountMin,
                                System: gg.Details[0].CanBePlayedAsSystemBet,
                                StakeMax: gg.Details[0].StakeMax,
                                Details: gg.Details.map((gameDetail, gameDetailIdx) => {
                                    if (defaultEventId && !groupIdx && !gameIdx && !gameDetailIdx) {
                                        delete self.defaults;
                                        self.defaults = eventDetailKeyModel.create(<SnapshotOut<typeof eventDetailKeyModel>>{
                                            eventId: defaultEventId,
                                            detailId: gameDetail.ID,
                                            isLive: false
                                        });
                                    }
                                    self.highlightedTips = observable(
                                        self.highlightedTips.concat(gameDetail.OddsList.filter(x => x.TicketOfDayTip).map(tip => createTipIdent(convertOdds2OddListItem(eventId, gg.ID, gameDetail.ID, tip))))
                                    );

                                    return gameDetailStore.create(<SnapshotOut<typeof gameDetailStore>>{
                                        ID: gameDetail.ID,
                                        Name: gameDetail.Name,
                                        LineNumber: gameDetail.LineNumber,
                                        Odds: createOdds(gameDetail.OddsList.map(odd => convertOdds2OddListItem(eventId, gg.ID, gameDetail.ID, odd)), businessType, gameDetail.Name, gg.ID, gameTipRenameData)
                                    });
                                }),

                                Info: null
                            });
                        }
                        )
                    })
                );
            },

            /**
             * Returns event instance for update of rates from list self.events.
             * To use listEventGameDetailProviderForUpdate, override to return null
             */
            listEventProviderForUpdate(eventId: number): IEventDataStore {
                return self.events.find(evt => evt.Id === eventId);
            },

            /**
             * Returns event instance for update of rates from specific list of descendant.
             */
            listEventGameDetailProviderForUpdate(_eventId: number): IgameDetailStore[] {
                return null;
            }
        }
    })
    .actions(self => {
        const env = getEnv<IAppEnv>(self);

        const showDetailAsync: (params: { eventKey: string, isLive: boolean }, canUpdateListRates: boolean) => Promise<void> = flow(function* (params: { eventKey: string, isLive: boolean }, canUpdateListRates: boolean) {

            if (params.isLive) {

                cleanDetail();

                if (!self.liveDetail) {
                    self.liveDetail = LiveEventStore.create({}, env);
                }

                self.liveDetail.startForNewEvent(Number(params.eventKey));

                self.isDetailShown = true;
                return;
            }

            cleanLiveDetail();

            const e = getEventDetailParamFromKey(params.eventKey)
            if (!self.detail) {
                self.detail = eventDetailStore.create(<SnapshotOut<typeof eventDetailStore>>
                    {
                        eventId: e.eventId,
                        categoryId: e.categoryId,
                        Name: '',
                        GameGroups: []
                    },
                    env
                );
            }
            self.isDetailShown = true;
            yield self.detail.loadAsync(params);
            if (canUpdateListRates) {
                updateRatesFromDetail();
            }
        });

        const reloadDetailAsync: (canUpdateListRates: boolean) => Promise<void> = flow(function* (canUpdateListRates: boolean) {
            if (self.detail) {
                yield self.detail.reloadAsync();
                if (canUpdateListRates) {
                    updateRatesFromDetail();
                }
            }
        });

        /**
         * Updates rate of event in list from data of event detail.
         * Source of list of events depends on descendant - either in self.events for hotbets/topbets (listEventProviderForUpdate) or specific gamedetails (see listEventGameDetailProviderForUpdate)
         *
         */
        const updateRatesFromDetail = (): void => {
            if (self.detail && self.detail.state === ProgressStateModel.Success) {

                //#region update of hotbets/topbets lists
                let eventInList = self.listEventProviderForUpdate(self.detail.eventId);
                if (eventInList) {
                    if (eventInList.GameGroups) {
                        eventInList.GameGroups.forEach(ggDest => {
                            if (!ggDest.Games || !self.detail.GameGroups) {
                                return;
                            }
                            let ggInSource = self.detail.GameGroups.find(ggS => ggS.ID === ggDest.ID);
                            if (ggInSource) {
                                ggDest.Games.forEach(gameD => {
                                    if (!gameD.Details || !ggInSource.Games) {
                                        return;
                                    }
                                    let gInSource = ggInSource.Games.find(gS => gS.ID === gameD.ID);
                                    if (gInSource) {
                                        gameD.Details.forEach(detailD => {
                                            if (!detailD.Odds || !gInSource.Details) {
                                                return;
                                            }
                                            let dInS = gInSource.Details.find(dS => dS.ID === detailD.ID);
                                            if (!dInS || !dInS.Odds) {
                                                return;
                                            }

                                            copyRatesInGameDetail(detailD, dInS);
                                        });
                                    }
                                });
                            }
                        });
                    }
                    return;
                }
                //#endregion

                //#region update of common list of events
                let listGameDetailsData: IgameDetailStore[] = self.listEventGameDetailProviderForUpdate(self.detail.eventId);
                if (!listGameDetailsData || listGameDetailsData.length === 0) {
                    return;
                }

                listGameDetailsData.forEach(listGameDetail => {

                    let gameId: string = null;

                    let sourceGameDetailOddsKeys = Object.keys(listGameDetail.Odds);
                    for (let key of sourceGameDetailOddsKeys) {

                        let ident;
                        let item = listGameDetail.Odds[key];
                        if (!item) { // threw an null exception, adding this but don't know consequences
                            continue;
                        }
                        if (isObservableArray(item)) {
                            if (!(item.length > 0 && item[0].ident && item[0].rate)) { //item is OddModel
                                continue;
                            }
                            ident = item[0].ident;
                        }
                        else if (item.ident && item.rate) { //is OddModel
                            ident = item.ident;
                        }
                        else {
                            continue;
                        }
                        let parsedIdent = parseTipIdent(ident);
                        gameId = parsedIdent.gameID;
                        break;
                    }
                    if (!gameId) {
                        return;
                    }

                    let gameS: Instance<typeof gameStore> = null;
                    self.detail.GameGroups.some(ggS => {
                        gameS = ggS.Games.find(g => g.ID === gameId);
                        return gameS ? true : false;
                    });
                    if (!gameS) {
                        return;
                    }

                    let dInS = gameS.Details.find(dS => dS.ID === listGameDetail.ID);
                    if (!dInS) {
                        return;
                    }

                    copyRatesInGameDetail(listGameDetail, dInS);
                });

                //#endregion
            }
        };

        const copyRatesInGameDetail = (detailD: IgameDetailStore, dInS: IgameDetailStore): void => {
            //see gameDetailStore, union over odds, it's either OddModel, array of OddModel or something else

            if (isOdds1x2(detailD.Odds) && isOdds1x2(dInS.Odds)) {
                detailD.Odds.copyRatesFrom(dInS.Odds);
            } else if (isTipList(detailD.Odds) && isTipList(dInS.Odds)) {
                detailD.Odds.copyRatesFrom(dInS.Odds);
            } else if (isBelowOver(detailD.Odds) && isBelowOver(dInS.Odds)) {
                detailD.Odds.copyRatesFrom(dInS.Odds);
            } else if (isHandicap1x2(detailD.Odds) && isHandicap1x2(dInS.Odds)) {
                detailD.Odds.copyRatesFrom(dInS.Odds);
            } else if (isSpreadOrHandicap12(detailD.Odds) && isSpreadOrHandicap12(dInS.Odds)) {
                detailD.Odds.copyRatesFrom(dInS.Odds);
            } else {
                env.loggingService.error("Unexpected odds type to copy");
            }
        }

        const hideDetail = () => {
            cleanDetail();
            cleanLiveDetail();
            self.isDetailShown = false;
        };

        const cleanDetail = () => {
            if (!self.detail) {
                return;
            }
            delete self.detail;
        }

        const cleanLiveDetail = () => {
            if (!self.liveDetail) {
                return;
            }
            self.liveDetail.clean();
            delete self.liveDetail;
        }

        const toggleDetailAsync: (params: { eventKey: string, isLive: boolean }) => Promise<void> = flow(function* (params: { eventKey: string, isLive: boolean }) {
            const { eventId } = getEventDetailParamFromKey(params.eventKey);

            if (!eventId || ((self.detail && self.detail.key === params.eventKey && !params.isLive) || (self.liveDetail && `${self.liveDetail.id}` === params.eventKey && params.isLive))) {
                hideDetail();
            }
            else {
                yield showDetailAsync(params, true);
            }
        });

        const setDefaults = (params: Instance<typeof eventDetailKeyModel>) => {
            if (self.defaults) delete self.defaults;
            self.defaults = eventDetailKeyModel.create(<SnapshotOut<typeof eventDetailKeyModel>>
                {
                    eventId: params.eventId,
                    detailId: params.detailId,
                    isLive: params.isLive
                },
                env
            );
        };

        const resetDefaults = () => {
            delete self.defaults;
            cleanDetail();
            cleanLiveDetail();
        };

        return {
            toggleDetailAsync,
            showDetailAsync,
            reloadDetailAsync,
            hideDetail,
            resetDefaults,
            setDefaults,
            countDown: () => {
                self.events.forEach(event => event.countDown(false));
            }
        };
    });
